import { useFlag } from '@reflag/react-sdk';
import * as React from 'react';

import * as analytics from '@gitbook/app-analytics/client';
import * as hiveCore from '@gitbook/hive-core';
import { useHiveBranch } from '@gitbook/hive-react';
import { createPalette, type PaletteDef } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { DialogRoot } from '@gitbook/spine-react';
import { assertIsDefined, filterOutNullable } from '@gitbook/util-ts';

import {
    CreateContentSourceOpenAPIDialog,
    CreateContentSourceTranslationDialog,
    ImportInSpaceDialog,
    useContentNavigation,
    usePickIntegrationContentSourcePalette,
} from '../../../Components';
import { useSpaceContext } from '../context';
import { InsertLinkModal } from './InsertLinkModal';

type InsertPage = (
    page: hiveCore.RevisionPage | ((change: hiveCore.RevisionChange) => hiveCore.RevisionPage)
) => void;

/**
 * Register commands relative to the page.
 */
export function useInsertPageCommands(
    props: { parent?: string } = {}
): [React.ReactElement, PaletteDef, InsertPage] {
    const { parent } = props;

    const { space, analyticsContext } = useSpaceContext();
    const { navigateToPage } = useContentNavigation();
    const [modal, setModal] = React.useState<
        'link' | 'import' | 'computed-openapi' | 'computed-translations' | null
    >(null);
    const hiveBranch = useHiveBranch();
    const computedContentFeature = useFlag('COMPUTED_CONTENT');
    const translationsFeature = useFlag('TRANSLATIONS');
    const [integrationContentSourceElement, getIntegrationContentSourcePalette] =
        usePickIntegrationContentSourcePalette({
            organizationId: space.organization,
            spaceId: space.id,
            onSubmit: (source) => {
                insertPage(hiveCore.createPageComputed({ title: source.type, computed: source }));
            },
        });

    /** Insert a new page under this page */
    const insertPage: InsertPage = React.useCallback(
        (
            page:
                | hiveCore.RevisionPage
                | ((change: hiveCore.RevisionChange) => hiveCore.RevisionPage)
        ) => {
            assertIsDefined(hiveBranch);

            const newPage = hiveBranch.syncChange((change) => {
                const toInsert = typeof page === 'function' ? page(change) : page;
                change.insertPage(toInsert, {
                    into: parent,
                });

                return toInsert;
            });

            if (newPage.type === hiveCore.RevisionPageType.Computed) {
                switch (newPage.computed.type) {
                    case 'builtin:openapi':
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'edit_create_page',
                            pageKind: newPage.type,
                            sourceType: newPage.computed.type,
                            openAPIModelsPage: newPage.computed.props.models,
                        });
                        break;
                    case 'builtin:translation':
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'edit_create_page',
                            pageKind: newPage.type,
                            sourceType: newPage.computed.type,
                        });
                        break;
                    default:
                        analytics.logEvent({
                            ...analyticsContext,
                            type: 'edit_create_page',
                            pageKind: newPage.type,
                            sourceType: newPage.computed.type,
                        });
                        break;
                }
            } else if (newPage.type === hiveCore.RevisionPageType.Link) {
                analytics.logEvent({
                    ...analyticsContext,
                    type: 'edit_create_page',
                    pageKind: newPage.type,
                    targetKind: newPage.target.kind,
                });
            } else {
                analytics.logEvent({
                    ...analyticsContext,
                    type: 'edit_create_page',
                    pageKind: newPage.type,
                });
            }
            if (
                newPage.type !== hiveCore.RevisionPageType.Link &&
                newPage.type !== hiveCore.RevisionPageType.Computed
            ) {
                navigateToPage(hiveBranch.revision, newPage.id, { preserveSideSheet: true });
            }
        },
        [hiveBranch, navigateToPage, analyticsContext, parent]
    );

    /** Insert a group page */
    const insertGroupPage = React.useCallback(() => {
        assertIsDefined(hiveBranch);

        const newPage = hiveBranch.syncChange((change) => {
            const pageGroup = hiveCore.createPageGroup({
                title: change.getUniquePageTitle('Group', true),
            });

            const pageDocument = hiveCore.createPageDocument({
                title: change.getUniquePageTitle('Page', true),
            });

            change.insertPage(pageGroup, {
                into: parent,
            });
            change.insertPage(pageDocument, {
                into: pageGroup.id,
            });

            return pageDocument;
        });

        analytics.logEvent({
            ...analyticsContext,
            type: 'edit_create_page',
            pageKind: hiveCore.RevisionPageType.Group,
        });

        navigateToPage(hiveBranch.revision, newPage.id, { preserveSideSheet: true });
    }, [hiveBranch, navigateToPage, parent, analyticsContext]);

    const palette = React.useMemo(
        () =>
            createPalette({
                groups: [
                    {
                        key: 'insertPage',
                        commands: [
                            {
                                key: 'insertPage.sheet',
                                icon: Icon.Page,
                                title: 'Page',
                                testID: 'insert-page-button',
                                trigger: () => {
                                    insertPage((change) => {
                                        return hiveCore.createPageDocument({
                                            title: change.getUniquePageTitle('Page', true),
                                        });
                                    });
                                },
                            },
                            parent
                                ? null
                                : {
                                      key: 'insertPage.group',
                                      icon: Icon.AddPageGroup,
                                      title: 'Group',
                                      trigger: () => {
                                          insertGroupPage();
                                      },
                                  },
                            {
                                key: 'insertPage.link',
                                icon: Icon.Link,
                                title: 'Link to...',
                                trigger: () => {
                                    setModal('link');
                                },
                            },
                        ].filter(filterOutNullable),
                    },
                    {
                        key: 'insertComputed',
                        commands: [
                            {
                                key: 'insertComputed.openapi',
                                icon: Icon.OpenAPI,
                                title: 'OpenAPI Reference',
                                trigger: () => {
                                    setModal('computed-openapi');
                                },
                            },
                            translationsFeature.isEnabled
                                ? {
                                      key: 'insertComputed.translations',
                                      icon: Icon.AITranslate,
                                      title: 'Translation',
                                      trigger: () => {
                                          setModal('computed-translations');
                                      },
                                  }
                                : null,
                            computedContentFeature.isEnabled
                                ? {
                                      key: 'insertComputed.automations',
                                      icon: Icon.Integrations,
                                      title: 'Integrations',
                                      descendant: getIntegrationContentSourcePalette,
                                  }
                                : null,
                        ].filter(filterOutNullable),
                    },
                    {
                        key: 'insertExisting',
                        commands: [
                            {
                                key: 'insertExisting.import',
                                icon: Icon.Download,
                                title: 'Import pages',
                                trigger: () => {
                                    setModal('import');
                                },
                            },
                        ].filter(filterOutNullable),
                    },
                ].filter(filterOutNullable),
            }),
        [
            parent,
            insertGroupPage,
            insertPage,
            computedContentFeature.isEnabled,
            translationsFeature.isEnabled,
            getIntegrationContentSourcePalette,
        ]
    );

    const onSubmitLink = (page: hiveCore.RevisionPageLink) => {
        insertPage(page);
        setModal(null);
    };

    return [
        <>
            {integrationContentSourceElement}

            <DialogRoot
                key="link"
                isOpen={modal === 'link'}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setModal(null);
                    }
                }}
            >
                <InsertLinkModal isOpen={modal === 'link'} onSubmit={onSubmitLink} />
            </DialogRoot>

            <DialogRoot
                key="import"
                isOpen={modal === 'import'}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setModal(null);
                    }
                }}
            >
                <ImportInSpaceDialog page={parent} />
            </DialogRoot>

            <DialogRoot
                key="computed-openapi"
                isOpen={modal === 'computed-openapi'}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setModal(null);
                    }
                }}
            >
                <CreateContentSourceOpenAPIDialog
                    organizationId={space.organization}
                    onSubmit={(source) => {
                        insertPage(
                            hiveCore.createPageComputed({ title: 'OpenAPI', computed: source })
                        );
                    }}
                />
            </DialogRoot>

            <DialogRoot
                key="computed-translations"
                isOpen={modal === 'computed-translations'}
                onOpenChange={(isOpen) => {
                    if (!isOpen) {
                        setModal(null);
                    }
                }}
            >
                <CreateContentSourceTranslationDialog
                    organizationId={space.organization}
                    analyticsContext={analyticsContext}
                    onSubmit={async (source) => {
                        insertPage(
                            hiveCore.createPageComputed({ title: 'Translation', computed: source })
                        );

                        // Wait for the translations to be done
                        await hiveBranch?.waitForComputed();
                    }}
                />
            </DialogRoot>
        </>,
        palette,
        insertPage,
    ];
}
